import { TABLE_COL_MIN_WIDTH } from "@library-elements/table";

/**
 * Calculate the auto-fit width for a specific column.
 * Measures the max content width across all cells in the column,
 * respects TABLE_COL_MIN_WIDTH (raised per column by `colFloorsPx`, the
 * in-cell element floors), and returns adjusted widths for all columns.
 */
export const calculateAutoFitWidth = (
  colIndex: number,
  tableCols: number,
  tableWrapperEl: HTMLElement,
  currentColWidths: number[],
  colFloorsPx?: number[],
): number[] => {
  const wrapperWidth = tableWrapperEl.getBoundingClientRect().width;
  if (wrapperWidth <= 0) return [...currentColWidths];

  const minPxFor = (i: number): number =>
    Math.max(TABLE_COL_MIN_WIDTH, colFloorsPx?.[i] ?? 0);
  const minPercentFor = (i: number): number =>
    (minPxFor(i) / wrapperWidth) * 100;

  // Find max content width in the target column
  const cells = tableWrapperEl.querySelectorAll(
    `[data-cell-col="${colIndex}"] .table-cell-content`,
  );

  let maxContentWidth = minPxFor(colIndex);
  cells.forEach((cell) => {
    maxContentWidth = Math.max(maxContentWidth, cell.scrollWidth);
  });

  // Convert to percentage
  const autoFitPercent = Math.max(
    minPercentFor(colIndex),
    (maxContentWidth / wrapperWidth) * 100,
  );

  // Calculate remaining percentage for other columns
  const oldColPercent = currentColWidths[colIndex] ?? 100 / tableCols;
  const diff = autoFitPercent - oldColPercent;
  const otherTotal = currentColWidths.reduce(
    (sum, w, i) => (i === colIndex ? sum : sum + w),
    0,
  );

  const newWidths = currentColWidths.map((w, i) => {
    if (i === colIndex) return autoFitPercent;
    if (otherTotal <= 0) return w;
    // Redistribute proportionally, clamped to minimum
    return Math.max(minPercentFor(i), w - (diff * w) / otherTotal);
  });

  // Clamping to the per-col minimum can push the total over 100%. Rescale the
  // non-auto-fit columns so the row sums back to 100, while still respecting
  // the floor — repeated until convergence (or a fixed cap to avoid loops on
  // pathological inputs).
  for (let pass = 0; pass < 8; pass++) {
    const total = newWidths.reduce((s, w) => s + w, 0);
    const drift = total - 100;
    if (Math.abs(drift) < 0.01) break;

    const adjustable: number[] = [];
    let adjustableTotal = 0;
    for (let i = 0; i < newWidths.length; i++) {
      if (i === colIndex) continue;
      if (drift > 0 && newWidths[i] <= minPercentFor(i) + 0.01) continue;
      adjustable.push(i);
      adjustableTotal += newWidths[i];
    }
    if (adjustable.length === 0 || adjustableTotal <= 0) break;

    for (const i of adjustable) {
      const share = newWidths[i] / adjustableTotal;
      newWidths[i] = Math.max(minPercentFor(i), newWidths[i] - drift * share);
    }
  }

  return newWidths;
};
